import {
    Component,
    OnInit,
    ViewEncapsulation,
    forwardRef,
    Input,
    Output,
    EventEmitter,
    TemplateRef,
} from '@angular/core';
import {
    NG_VALUE_ACCESSOR,
    ControlValueAccessor,
    FormGroupDirective,
    ControlContainer,
    AbstractControl,
} from '@angular/forms';
import { ReactiveFormsService } from '../../providers';

@Component({
    selector: 'roblex-input-form-field',
    templateUrl: './input-form-field.component.html',
    styleUrls: ['./input-form-field.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => InputFormFieldComponent),
        },
    ],
})
export class InputFormFieldComponent implements OnInit, ControlValueAccessor {
    /**
     * Hide  of input form field component
     */
    hide = true;
    /**
     * Control  of input form field component
     */
    control: AbstractControl;
    /**
     * Value  of input field component
     */
    _value = '';
    /**
     * Input identifier of input form field component
     */
    @Input() identifier: string;
    /**
     * Input class name of input form field component
     */
    @Input() className: string;
    /**
     * Input  of input form field component
     */
    @Input() appearance: 'standard' | 'fill' | 'outline' = 'outline';
    /**
     * Input  of input form field component
     */
    @Input() formControlName: string;
    /**
     * Input  of input field component
     */
    @Input() label = 'input form field label';
    /**
     * Input  of input field component
     */
    @Input() disabled: boolean = false;
    /**
     * Input  of input field component
     */
    @Input() placeholder = '';
    /**
     * Input  of input form field component
     */
    @Input() type = 'text';
    /**
     * Input icon left of input form field component
     */
    @Input() iconLeft: string;
    /**
     * Input icon right of input form field component
     */
    @Input() iconRight: string;
    /**
     * Input icon right disabled of input form field component
     */
    @Input() iconRightDisabled: boolean | null;
    /**
     * Input text right of input form field component
     */
    @Input() templateRight: TemplateRef<any>;
    /**
     * Input enabled number only of input form field component
     */
    @Input() enabledNumberOnly = false;
    /**
     * Input template legend of input form field component
     */
    @Input() templateLegend: TemplateRef<any>;
    /**
     * Input clear all text by backspace press of input form field component
     */
    @Input() hasBackspace = false;
    /**
     * Input upper case of input form field component
     */
    @Input() upperCase = false;
    /**
     * Output suffix of input form field component
     */
    @Output() suffix: EventEmitter<any> = new EventEmitter();
    /**
     * Output clear text input of input form field component
     */
    @Output() backspace: EventEmitter<any> = new EventEmitter();
    /**
     * Gets value
     */
    get value() {
        return this._value;
    }
    /**
     * Sets value
     */
    set value(val) {
        this._value = val;
        this.onChangeCallback(this._value);
    }
    /**
     * Gets form
     */
    get form() {
        return this.controlContainer?.formDirective
            ? (this.controlContainer?.formDirective as FormGroupDirective)?.form
            : null;
    }

    get classFormFiel() {
        const classList = {};

        classList['mat-form-field-invalid'] = this.control?.touched && this.control?.invalid

        if (this.className) {
            classList[this.className] = true;
        }

        return classList;
    }
    /**
     * Determines whether touched callback on
     */
    onTouchedCallback: () => void;
    /**
     * Propagate change of input field component
     */
    onChangeCallback = (_: any) => {};
    /**
     * Creates an instance of input form field component.
     * @param controlContainer ControlContainer
     */
    constructor(public controlContainer: ControlContainer) {}
    /**
     * on init
     */
    ngOnInit(): void {
        this.control = this.form.get(this.formControlName);
    }
    /**
     * Gets error message
     */
    getErrorMessage() {
        if (this.control) {
            return ReactiveFormsService?.errorMessage(this.control);
        }
        return null;
    }
    /**
     * Determines whether click mat suffix on
     */
    onClickMatSuffix() {
        this.suffix.emit();
    }
    /**
     * Determines whether change event on
     * @param value any
     */
    onKeyupEvent(event: any) {
        if (this.hasBackspace && (event.code === "Backspace" || event.key === "Backspace")) {
            this.backspace.emit();
        } else {
            const value = event.target.value;
            this.writeValue(value != undefined ? value : null);
        }
    }
    /**
     * Determines whether blur event on
     */
    onBlurEvent() {
        if (this.control) {
            this.control.markAsTouched();
        }
    }
    /**
     * Writes value
     * @param value any
     */
    writeValue(value: any): void {
        this.value = value;
        this.onChangeCallback(this.value);
    }
    /**
     * Registers on change
     * @param fn any
     */
    registerOnChange(fn: any): void {
        this.onChangeCallback = fn;
    }
    /**
     * Registers on touched
     * @param fn any
     */
    registerOnTouched(fn: any): void {
        this.onTouchedCallback = fn;
    }
    /**
     * Sets disabled state
     * @param isDisabled boolean
     */
    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }
}
